/**
 * Template-specific table component
 * Renders a table with dynamic columns based on template field definitions
 */
import { useMemo, useState, useEffect, useRef } from 'react'
import { getFieldValueFromReport, formatFieldValue } from '../utils/templateFieldParser'
import ResizableHeader from './ResizableHeader'
import ViewInACCButton from './ViewInACCButton'
import ActionsColumn from './ActionsColumn'
import { LocationDropdown } from './LocationComponents'
import EditModal from './EditModal'

const TemplateTable = ({
  templateId,
  templateName,
  fieldDefinitions, // { fields: [], fieldMap: {} }
  templateConfig, // Template configuration with choiceFields, toggleFields, etc.
  reports,
  projectId,
  permissions = {}, // { canView, canEdit, canApprove, canReject, hasReviewerAccess }
  sortKey,
  sortDir,
  handleSort,
  selectedMap,
  toggleSelect,
  toggleSelectAll, // Function to toggle select all for this template's reports
  editingRow,
  editedValues,
  handleFieldChange,
  rowLoading,
  rowAction,
  handleApproveRow,
  handleRejectRow,
  handleEditRow,
  handleSaveEdit,
  handleCancelEdit,
  columnWidths,
  handleResizeStart,
  // Location-related props
  locationMap,
  locationsList,
  locationDropdownOpen,
  setLocationDropdownOpen,
  expandedLocations,
  setExpandedLocations,
  locationTriggerRef,
  // Asset mapping: equipment number (ca1) -> clientAssetId
  assetMap = {},
  // Direct form->asset mapping from ACC Relationships API: formId -> clientAssetId
  formAssetMap = {}
}) => {
  // Collapsible state - default to expanded
  const [isCollapsed, setIsCollapsed] = useState(false)

  // Mobile detection and modal state
  const [isMobile, setIsMobile] = useState(false)
  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingReport, setEditingReport] = useState(null)

  // Expanded cards state - tracks which cards are expanded on mobile
  const [expandedCards, setExpandedCards] = useState({})

  // Sticky header state
  const [isHeaderSticky, setIsHeaderSticky] = useState(false)
  const tableRef = useRef(null)
  const theadRef = useRef(null)

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Open modal when editingRow changes on mobile
  useEffect(() => {
    if (isMobile && editingRow) {
      const report = reports.find(r => r.id === editingRow)
      if (report) {
        setEditingReport(report)
        setIsEditModalOpen(true)
      }
    } else {
      setIsEditModalOpen(false)
      setEditingReport(null)
    }
  }, [editingRow, isMobile, reports, editedValues])

  // Sticky header scroll listener - detect scroll within table container
  useEffect(() => {
    const tableWrap = tableRef.current

    if (!tableWrap) return

    const handleScroll = () => {
      // Check if user has scrolled within the table container
      const scrollTop = tableWrap.scrollTop
      const isScrolled = scrollTop > 0

      setIsHeaderSticky(isScrolled)
    }

    // Listen to scroll events on the table wrapper (not window)
    tableWrap.addEventListener('scroll', handleScroll)

    // Check initial state
    handleScroll()

    return () => {
      tableWrap.removeEventListener('scroll', handleScroll)
    }
  }, [reports.length, isCollapsed])

  if (!fieldDefinitions || !fieldDefinitions.fields) {
    return (
      <div style={{ padding: '1rem', textAlign: 'center', color: '#999' }}>
        No field definitions available for this template
      </div>
    )
  }

  const fields = fieldDefinitions.fields

  // Handle modal save
  const handleModalSave = (report) => {
    handleSaveEdit(report)
    setIsEditModalOpen(false)
    setEditingReport(null)
  }

  // Handle modal cancel
  const handleModalCancel = () => {
    handleCancelEdit()
    setIsEditModalOpen(false)
    setEditingReport(null)
  }

  // Compute allSelected based on reports passed to this template table
  const allSelected = useMemo(() => {
    if (reports.length === 0) return false
    return reports.every(r => selectedMap[r.id])
  }, [reports, selectedMap])

  // Use the explicit equipmentNumberFieldId from templateConfig (set per-template in projects.js)
  // This is more reliable than label-matching since field labels can vary
  const equipmentNumberFieldId = templateConfig?.equipmentNumberFieldId || null

  // Helper to get the asset clientAssetId for a given report
  // Primary: use formAssetMap (ACC Relationships API - direct form->asset link)
  // Fallback: match Equipment Number field value against assetMap (ca1 attribute)
  const getAssetClientId = (report) => {
    // Primary lookup: direct form->asset relationship from Relationships API
    if (formAssetMap && formAssetMap[report.id]) {
      return formAssetMap[report.id]
    }

    // Fallback: match Equipment Number field value against ca1 in assetMap
    if (!equipmentNumberFieldId || !assetMap || Object.keys(assetMap).length === 0) return null
    const customValues = report?.nativeForm?.customValues || []
    const fieldEntry = customValues.find(cv => (cv.fieldId || cv.id) === equipmentNumberFieldId && !cv.deleted)
    if (!fieldEntry) return null
    const equipNum = fieldEntry.textVal ?? fieldEntry.numberVal ?? fieldEntry.choiceVal ?? null
    if (equipNum === null || equipNum === undefined) return null
    return assetMap[String(equipNum).trim()] || null
  }

  // Calculate if any checkboxes are selected for bulk operations
  const isAnyCheckboxSelected = useMemo(() => {
    return Object.values(selectedMap).some(selected => selected)
  }, [selectedMap])

  // Calculate the count of selected forms
  const selectedCount = useMemo(() => {
    return Object.values(selectedMap).filter(selected => selected).length
  }, [selectedMap])

  return (
    <>
      {/* Edit Modal for Mobile */}
      {isMobile && (
        <EditModal
          isOpen={isEditModalOpen}
          report={editingReport}
          fields={fields}
          templateConfig={templateConfig}
          editedValues={editingReport ? editedValues[editingReport.id] || {} : {}}
          handleFieldChange={handleFieldChange}
          handleSave={handleModalSave}
          handleCancel={handleModalCancel}
          locationMap={locationMap}
          locationsList={locationsList}
          locationDropdownOpen={locationDropdownOpen}
          setLocationDropdownOpen={setLocationDropdownOpen}
          expandedLocations={expandedLocations}
          setExpandedLocations={setExpandedLocations}
          getFieldValueFromReport={getFieldValueFromReport}
          formatFieldValue={formatFieldValue}
        />
      )}

      <div style={{ marginBottom: '2rem' }}>
        {/* Template Header with Collapse Toggle */}
        <div
          className={`template-table-header ${isCollapsed ? 'collapsed' : 'expanded'}`}
          role="button"
          aria-expanded={!isCollapsed}
          aria-label={`${isCollapsed ? 'Expand' : 'Collapse'} ${templateName} table`}
        >
          {/* Select All Checkbox - only show if user has reviewer access */}
          {permissions.hasReviewerAccess && (
            <input
              type="checkbox"
              className="template-header-checkbox"
              aria-label="Select all forms in this template"
              checked={allSelected}
              onChange={(e) => {
                e.stopPropagation()
                toggleSelectAll(reports)
              }}
              onClick={(e) => e.stopPropagation()}
            />
          )}

          {/* Collapse/Expand Icon */}
          <span
            className={`template-collapse-icon ${isCollapsed ? 'collapsed' : 'expanded'}`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            ▼
          </span>
          <span onClick={() => setIsCollapsed(!isCollapsed)}>📋</span>
          <span onClick={() => setIsCollapsed(!isCollapsed)}>{templateName}</span>
        <span className="template-form-count" onClick={() => setIsCollapsed(!isCollapsed)}>
          {reports.length} form{reports.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Table - Only show when not collapsed */}
      {!isCollapsed && (
      <>
      <div className="approval-summary-table-wrap" ref={tableRef}>
        <table className="approval-summary-table header-sticky" role="table">
          <thead ref={theadRef} className={isHeaderSticky ? 'sticky' : ''}>
            <tr>
              {/* Select All Checkbox - only show if user has reviewer access */}
              {permissions.hasReviewerAccess && (
                <th className="approval-summary-table" style={{ width: '50px' }}>
                  <input
                    type="checkbox"
                    aria-label="Select all forms in this template"
                    checked={allSelected}
                    onChange={() => toggleSelectAll(reports)}
                  />
                </th>
              )}

              {/* Asset column - shows matched clientAssetId from Autodesk Assets API */}
              <ResizableHeader
                columnKey="asset"
                style={{ minWidth: '200px' }}
                columnWidths={columnWidths}
                onResizeStart={handleResizeStart}
              >
                Asset
              </ResizableHeader>

              {/* Location column */}
              <ResizableHeader
                columnKey="location"
                style={{ minWidth: '150px' }}
                columnWidths={columnWidths}
                onResizeStart={handleResizeStart}
              >
                Location
              </ResizableHeader>

              {/* Form Date column */}
              <ResizableHeader
                columnKey="formDate"
                style={{ width: '120px', cursor: 'pointer' }}
                onClick={() => handleSort('formDate')}
                columnWidths={columnWidths}
                onResizeStart={handleResizeStart}
              >
                Form Date {sortKey === 'formDate' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
              </ResizableHeader>

              {/* Dynamic field columns from template */}
              {fields.map((field, idx) => (
                <ResizableHeader
                  key={field.fieldId}
                  columnKey={`field-${field.fieldId}`}
                  style={{ minWidth: '120px' }}
                  columnWidths={columnWidths}
                  onResizeStart={handleResizeStart}
                >
                  {field.label}
                  {field.sectionLabel && (
                    <div style={{ fontSize: '0.75rem', fontWeight: 400, color: '#64748b' }}>
                      {field.sectionLabel}
                    </div>
                  )}
                </ResizableHeader>
              ))}

              {/* Form # column */}
              <ResizableHeader
                columnKey="formNum"
                style={{ width: '80px', cursor: 'pointer' }}
                onClick={() => handleSort('formNum')}
                columnWidths={columnWidths}
                onResizeStart={handleResizeStart}
              >
                Form # {sortKey === 'formNum' ? (sortDir === 'asc' ? '↑' : '↓') : '⇅'}
              </ResizableHeader>

              {/* View in ACC column */}
              <ResizableHeader
                columnKey="viewInACC"
                style={{ minWidth: '120px', textAlign: 'center' }}
                columnWidths={columnWidths}
                onResizeStart={handleResizeStart}
              >
                View in ACC
              </ResizableHeader>

              {/* Actions column - only show if user has reviewer access */}
              {permissions.hasReviewerAccess && (
                <th className="approval-summary-table" style={{
                  minWidth: '310px',
                  width: '310px',
                  position: 'sticky',
                  right: 0,
                  backgroundColor: '#f8fafc',
                  boxShadow: '-2px 0 4px rgba(0, 0, 0, 0.05)',
                  zIndex: 2
                }}>Actions</th>
              )}
            </tr>
          </thead>
          <tbody>
            {reports.length === 0 && (
              <tr>
                <td className="approval-summary-table" colSpan={fields.length + (permissions.hasReviewerAccess ? 7 : 6)}>
                  No forms awaiting approval for this template.
                </td>
              </tr>
            )}

            {reports.map((report) => {
              const isLoading = !!rowLoading[report.id]
              const isSelected = !!selectedMap[report.id]
              const isEditing = editingRow === report.id

              return (
                <tr
                  key={report.id}
                  style={{
                    backgroundColor: '#fff',
                    opacity: isLoading ? 0.6 : 1
                  }}
                >
                  {/* Checkbox - only show if user has reviewer access */}
                  {permissions.hasReviewerAccess && (
                    <td className="approval-summary-table">
                      <input
                        type="checkbox"
                        aria-label={`Select form ${report.formNum || report.id}`}
                        checked={isSelected}
                        onChange={() => toggleSelect(report.id)}
                        disabled={isEditing}
                      />
                    </td>
                  )}

                  {/* Asset - matched clientAssetId from Autodesk Assets API */}
                  <td>
                    {(() => {
                      const clientAssetId = getAssetClientId(report)
                      return (
                        <span
                          className="approval-summary-td-content"
                          title={clientAssetId || '—'}
                        >
                          {clientAssetId || '—'}
                        </span>
                      )
                    })()}
                  </td>

                  {/* Location column */}
                  <td style={{ position: 'relative' }}>
                    {isEditing ? (
                      <div style={{ position: 'relative' }}>
                        <div
                          ref={locationTriggerRef}
                          onClick={() => setLocationDropdownOpen(!locationDropdownOpen)}
                          style={{
                            width: '100%',
                            padding: '6px 8px',
                            border: '1px solid #cbd5e1',
                            borderRadius: 4,
                            fontSize: '0.9rem',
                            backgroundColor: '#fff',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                          }}
                        >
                          <span style={{ color: editedValues[report.id]?.locationId ? '#334155' : '#94a3b8' }}>
                            {editedValues[report.id]?.locationId && locationMap[editedValues[report.id].locationId]
                              ? locationMap[editedValues[report.id].locationId]
                              : 'Select Location'}
                          </span>
                          <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                            {locationDropdownOpen ? '▲' : '▼'}
                          </span>
                        </div>
                        {locationDropdownOpen && (
                          <LocationDropdown
                            value={editedValues[report.id]?.locationId || ''}
                            onChange={(locId) => {
                              handleFieldChange(report.id, 'locationId', locId)

                              // If job number field is configured, sync it with location name
                              if (templateConfig?.jobNumberFieldId) {
                                const locationName = locationMap[locId] || ''
                                handleFieldChange(report.id, templateConfig.jobNumberFieldId, locationName)
                              }

                              setLocationDropdownOpen(false)
                            }}
                            locations={locationsList}
                            expandedLocations={expandedLocations}
                            setExpandedLocations={setExpandedLocations}
                            onClose={() => setLocationDropdownOpen(false)}
                            triggerRef={locationTriggerRef}
                          />
                        )}
                      </div>
                    ) : (
                      (() => {
                        let locationValue = '—'

                        // First try to get locationId and map it to location name
                        if (report.locationId && locationMap[report.locationId]) {
                          locationValue = locationMap[report.locationId]
                        }
                        // Fallback: Try multiple ways to get location directly
                        else if (report.location?.name) locationValue = report.location.name
                        else if (report.locationName) locationValue = report.locationName
                        else if (report.locationPath) locationValue = report.locationPath
                        else {
                          // Try to find location in customValues
                          const locationField = report.nativeForm?.customValues?.find(
                            field => field.definition?.name?.toLowerCase().includes('location') ||
                                     field.definition?.label?.toLowerCase().includes('location')
                          )
                          if (locationField?.value) locationValue = locationField.value
                        }

                        return <span className="approval-summary-td-content" title={locationValue}>{locationValue}</span>
                      })()
                    )}
                  </td>

                  {/* Form Date */}
                  <td className="approval-summary-table">
                    <span className="approval-summary-td-content">
                      {report.formDate ? new Date(report.formDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      }) : '—'}
                    </span>
                  </td>

                  {/* Dynamic field values */}
                  {fields.map((field) => {
                    const value = getFieldValueFromReport(report, field.fieldId, field.valueType)
                    const formattedValue = formatFieldValue(value, field.valueType)
                    const editValue = editedValues[report.id]?.[field.fieldId] ?? value

                    // Check if this is a choice field with options
                    const choiceOptions = templateConfig?.choiceFields?.[field.fieldId]

                    // Check if this is the job number field (read-only, synced with location)
                    const isJobNumberField = field.fieldId === templateConfig?.jobNumberFieldId

                    return (
                      <td key={field.fieldId} className="approval-summary-table">
                        {isEditing ? (
                          field.valueType === 'toggle_val' ? (
                            // Toggle field: Yes/No/NA selector
                            <select
                              value={editValue !== null && editValue !== undefined ? editValue : ''}
                              onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                backgroundColor: '#fff',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="">Select...</option>
                              <option value="0">No</option>
                              <option value="1">Yes</option>
                              <option value="2">NA</option>
                            </select>
                          ) : choiceOptions ? (
                            // Choice field: dropdown with predefined options
                            <select
                              value={editValue || ''}
                              onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                backgroundColor: '#fff',
                                cursor: 'pointer'
                              }}
                            >
                              <option value="">Select...</option>
                              {choiceOptions.map((option, idx) => (
                                <option key={idx} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          ) : (
                            // Regular input for other field types
                            <input
                              type={field.valueType === 'number_val' ? 'number' : 'text'}
                              value={editValue || ''}
                              onChange={(e) => handleFieldChange(report.id, field.fieldId, e.target.value)}
                              disabled={isJobNumberField}
                              style={{
                                width: '100%',
                                padding: '0.5rem',
                                border: '1px solid #cbd5e1',
                                borderRadius: '4px',
                                fontSize: '0.875rem',
                                backgroundColor: isJobNumberField ? '#f1f5f9' : '#fff',
                                cursor: isJobNumberField ? 'not-allowed' : 'text'
                              }}
                              title={isJobNumberField ? 'Job Number is synced with Location' : ''}
                            />
                          )
                        ) : (
                          <span className="approval-summary-td-content">
                            {formattedValue}
                          </span>
                        )}
                      </td>
                    )
                  })}

                  {/* Form # */}
                  <td style={{ fontWeight: 700, color: '#1e40af', fontSize: '0.95rem' }}>
                    <span className="approval-summary-td-content">{report.formNum ?? '—'}</span>
                  </td>

                  {/* View in ACC */}
                  <td style={{ textAlign: 'center' }}>
                    <ViewInACCButton report={report} projectId={projectId} templateId={templateId} />
                  </td>

                  {/* Actions - only show if user has reviewer access */}
                  {permissions.hasReviewerAccess && (
                    <ActionsColumn
                      report={report}
                      isEditing={isEditing}
                      isLoading={isLoading}
                      currentAction={rowAction[report.id]}
                      isAnyRowEditing={editingRow !== null}
                      isAnyCheckboxSelected={isAnyCheckboxSelected}
                      isThisRowSelected={isSelected}
                      selectedCount={selectedCount}
                      showEditButton={true}
                      permissions={permissions}
                      onSave={handleSaveEdit}
                      onCancel={handleCancelEdit}
                      onEdit={handleEditRow}
                      onApprove={handleApproveRow}
                      onReject={handleRejectRow}
                    />
                  )}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {/* Mobile Cards View - Hidden on desktop, shown on mobile */}
      <div className="mobile-forms-cards" style={{ backgroundColor: '#f9fafb' }}>
        {reports.length === 0 ? (
          <div style={{
            padding: '2rem',
            textAlign: 'center',
            color: '#64748b',
            fontSize: '0.9rem',
            backgroundColor: '#fff',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            margin: '1rem'
          }}>
            No forms awaiting approval for this template.
          </div>
        ) : (
          reports.map((report) => {
          const isLoading = !!rowLoading[report.id]
          const isSelected = !!selectedMap[report.id]
          const isEditing = editingRow === report.id
          const clientAssetId = getAssetClientId(report)

          // Get location value
          let locationValue = '—'
          if (report.locationId && locationMap[report.locationId]) {
            locationValue = locationMap[report.locationId]
          } else if (report.location?.name) {
            locationValue = report.location.name
          } else if (report.locationName) {
            locationValue = report.locationName
          }

          // Check if this card is expanded
          const isExpanded = expandedCards[report.id] || false

          // Toggle expand/collapse for this card
          const toggleExpand = () => {
            setExpandedCards(prev => ({
              ...prev,
              [report.id]: !prev[report.id]
            }))
          }

          // Determine if actions should be enabled
          // Enable ONLY when: no selection OR this form is the only one selected
          const actionsDisabled =
            (selectedCount > 1) ||                    // Multiple selected - disable all
            (selectedCount === 1 && !isSelected) ||   // One selected but not this one - disable this
            isLoading ||
            isEditing

          // Show only first 4 fields when collapsed
          const fieldsToShow = isExpanded ? fields : fields.slice(0, 4)

          return (
            <div key={report.id} className="mobile-form-card">
              {/* Card Header */}
              <div className="mobile-form-card-header">
                <h4 className="mobile-form-card-title">
                  {clientAssetId || report.formNum || 'Form'}
                </h4>
                {permissions.hasReviewerAccess && (
                  <input
                    type="checkbox"
                    className="mobile-form-card-checkbox"
                    aria-label={`Select form ${report.formNum || report.id}`}
                    checked={isSelected}
                    onChange={() => toggleSelect(report.id)}
                    disabled={isEditing}
                  />
                )}
              </div>

              {/* Card Body - Show key fields */}
              <div className="mobile-form-card-body">
                {/* Asset */}
                {clientAssetId && (
                  <div className="mobile-form-field">
                    <div className="mobile-form-field-label">Asset</div>
                    <div className="mobile-form-field-value">{clientAssetId}</div>
                  </div>
                )}

                {/* Location */}
                <div className="mobile-form-field">
                  <div className="mobile-form-field-label">Location</div>
                  <div className="mobile-form-field-value">{locationValue}</div>
                </div>

                {/* Form Date */}
                <div className="mobile-form-field">
                  <div className="mobile-form-field-label">Form Date</div>
                  <div className="mobile-form-field-value">
                    {report.formDate ? new Date(report.formDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric'
                    }) : '—'}
                  </div>
                </div>

                {/* Dynamic fields - show first 4 or all based on expanded state */}
                {fieldsToShow.map((field) => {
                  const value = getFieldValueFromReport(report, field.fieldId, field.valueType)
                  const formattedValue = formatFieldValue(value, field.valueType)

                  return (
                    <div key={field.fieldId} className="mobile-form-field">
                      <div className="mobile-form-field-label">
                        {field.label}
                        {field.sectionLabel && (
                          <span style={{ fontSize: '0.65rem', fontWeight: 400, color: '#94a3b8', marginLeft: '0.25rem' }}>
                            ({field.sectionLabel})
                          </span>
                        )}
                      </div>
                      <div className="mobile-form-field-value">{formattedValue || '—'}</div>
                    </div>
                  )
                })}

                {/* Expand/Collapse Button - show if more than 4 fields */}
                {fields.length > 4 && (
                  <button
                    className="mobile-form-expand-btn"
                    onClick={toggleExpand}
                  >
                    {isExpanded ? '▲ Show Less' : `▼ Show ${fields.length - 4} More Fields`}
                  </button>
                )}

                {/* Form Number - always show at end */}
                <div className="mobile-form-field">
                  <div className="mobile-form-field-label">Form #</div>
                  <div className="mobile-form-field-value">{report.formNum || '—'}</div>
                </div>
              </div>

              {/* Card Actions */}
              {permissions.hasReviewerAccess && (
                <div className="mobile-form-card-actions">
                  <button
                    className="mobile-form-action-btn edit"
                    onClick={() => handleEditRow(report)}
                    disabled={actionsDisabled}
                    title={actionsDisabled && selectedCount > 0 ? "Select only this form to enable actions" : ""}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="mobile-form-action-btn approve"
                    onClick={() => handleApproveRow(report)}
                    disabled={actionsDisabled}
                    title={actionsDisabled && selectedCount > 0 ? "Select only this form to enable actions" : ""}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className="mobile-form-action-btn reject"
                    onClick={() => handleRejectRow(report)}
                    disabled={actionsDisabled}
                    title={actionsDisabled && selectedCount > 0 ? "Select only this form to enable actions" : ""}
                  >
                    ✕ Reject
                  </button>
                  <ViewInACCButton report={report} projectId={projectId} templateId={templateId} />
                </div>
              )}
            </div>
          )
        })
        )}
      </div>
      </>
      )}

      </div>
    </>
  )
}

export default TemplateTable
